import { useEffect } from 'react';

import { type UseAsyncDataResT, time } from '@dr.pogodin/react-utils';

import { getVideo } from 'services/video';

import {
  VIDEO_STATUS_REFRESH_INTERVAL,
  type VideoT,
  getId,
  useAsyncData,
} from 'utils';

/**
 * If given `id` is a string, it ensures the video with that ID is loaded into
 * the "editedVideo" segment of the global state, and returns that state segment;
 * otherwise, if `id` is null, it returns the "editedVideo" segment of the global
 * state as is.
 *
 * Also if the video stored in the "editedVideo" segment of the global state has
 * video data, but is not ready to be played, it periodically updates video data,
 * to track when it becomes ready to be played.
 */
export function useEditedVideo(id: null | string): UseAsyncDataResT<VideoT> {
  const res = useAsyncData(
    'editedVideo',
    (old): Promise<VideoT | null> | VideoT | null => (
      id && id !== old?.id ? getVideo(id).catch(() => null) : old
    ),
    { deps: [id], maxage: time.YEAR_MS },
  );

  useEffect(() => {
    // NOTE: Cannot use the object destructuring syntax here, as it confuses
    // the ESLint's rules of hooks, regarding the hook dependencies.
    const videoData = res.data;
    const videoReload = res.reload;

    let timerId: NodeJS.Timeout | undefined;

    if (
      videoData && !videoData.isReadyToBePlayed
      && (!id || id === videoData.id)
    ) {
      timerId = setTimeout(() => {
        void (async () => {
          const video = await getVideo(getId(videoData));
          await videoReload(() => video);
        })();
      }, VIDEO_STATUS_REFRESH_INTERVAL);
    }

    return () => {
      clearTimeout(timerId);
    };
  }, [id, res.data, res.reload]);

  return res;
}
